import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Save, RefreshCw, Settings, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EmailSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SMTP Configuration
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    use_tls: true
  });

  // Admin Notification Emails
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');

  // Auto-Reply Template
  const [autoReplyTemplate, setAutoReplyTemplate] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/email-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const settings = response.data;

      // Parse SMTP config
      const smtp = settings.find(s => s.setting_key === 'smtp_config');
      if (smtp) {
        setSmtpConfig(smtp.setting_value);
      }

      // Parse admin emails
      const admins = settings.find(s => s.setting_key === 'admin_notification_emails');
      if (admins) {
        setAdminEmails(admins.setting_value.emails || []);
      }

      // Parse auto-reply template
      const autoReply = settings.find(s => s.setting_key === 'contact_auto_reply');
      if (autoReply) {
        setAutoReplyTemplate(autoReply.setting_value.template || '');
      }

    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      toast({
        title: "Error",
        description: "Failed to load email settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSMTPConfig = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${API}/admin/email-settings`,
        {
          setting_key: 'smtp_config',
          setting_value: smtpConfig,
          is_active: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Success",
        description: "SMTP configuration saved successfully",
        variant: "default"
      });

    } catch (error) {
      console.error('Failed to save SMTP config:', error);
      toast({
        title: "Error",
        description: "Failed to save SMTP configuration",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveAdminEmails = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${API}/admin/email-settings`,
        {
          setting_key: 'admin_notification_emails',
          setting_value: { emails: adminEmails },
          is_active: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Success",
        description: "Admin notification emails saved successfully",
        variant: "default"
      });

    } catch (error) {
      console.error('Failed to save admin emails:', error);
      toast({
        title: "Error",
        description: "Failed to save admin emails",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveAutoReplyTemplate = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${API}/admin/email-settings`,
        {
          setting_key: 'contact_auto_reply',
          setting_value: { template: autoReplyTemplate },
          is_active: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Success",
        description: "Auto-reply template saved successfully",
        variant: "default"
      });

    } catch (error) {
      console.error('Failed to save auto-reply template:', error);
      toast({
        title: "Error",
        description: "Failed to save auto-reply template",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const addAdminEmail = () => {
    if (newAdminEmail && !adminEmails.includes(newAdminEmail)) {
      setAdminEmails([...adminEmails, newAdminEmail]);
      setNewAdminEmail('');
    }
  };

  const removeAdminEmail = (email) => {
    setAdminEmails(adminEmails.filter(e => e !== email));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Settings</h1>
          <p className="text-gray-600 mt-1">Configure email notifications and SMTP settings</p>
        </div>
        <Button onClick={fetchSettings} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Information Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Important Information</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Configure SMTP settings to enable email notifications</li>
                <li>Add admin emails to receive contact form notifications</li>
                <li>Customize auto-reply template sent to contact form submitters</li>
                <li>Use <code className="bg-blue-100 px-1 rounded">{'{name}'}</code> placeholder in templates</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            SMTP Configuration
          </CardTitle>
          <CardDescription>
            Configure SMTP server settings for sending emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host *
              </label>
              <Input
                type="text"
                placeholder="smtp.gmail.com"
                value={smtpConfig.host}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port *
              </label>
              <Input
                type="number"
                placeholder="587"
                value={smtpConfig.port}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username / Email *
              </label>
              <Input
                type="text"
                placeholder="your-email@gmail.com"
                value={smtpConfig.username}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password / App Password *
              </label>
              <Input
                type="password"
                placeholder="••••••••••••"
                value={smtpConfig.password}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email Address *
              </label>
              <Input
                type="email"
                placeholder="noreply@isde.academy"
                value={smtpConfig.from_email}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-2 pt-7">
              <input
                type="checkbox"
                id="use_tls"
                checked={smtpConfig.use_tls}
                onChange={(e) => setSmtpConfig({ ...smtpConfig, use_tls: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="use_tls" className="text-sm font-medium text-gray-700">
                Use TLS/STARTTLS
              </label>
            </div>
          </div>

          <div className="pt-4 border-t">
            <Button onClick={saveSMTPConfig} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              Save SMTP Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin Notification Emails */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Admin Notification Emails
          </CardTitle>
          <CardDescription>
            Email addresses to receive contact form notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="admin@isde.academy"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addAdminEmail()}
            />
            <Button onClick={addAdminEmail} variant="outline">
              Add Email
            </Button>
          </div>

          {adminEmails.length > 0 ? (
            <div className="space-y-2">
              {adminEmails.map((email, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">{email}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeAdminEmail(email)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No admin emails configured</p>
          )}

          <div className="pt-4 border-t">
            <Button onClick={saveAdminEmails} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              Save Admin Emails
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto-Reply Template */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Contact Form Auto-Reply Template
          </CardTitle>
          <CardDescription>
            Customize the email sent to people who submit the contact form. Use {'{name}'} for personalization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Template (HTML)
            </label>
            <Textarea
              value={autoReplyTemplate}
              onChange={(e) => setAutoReplyTemplate(e.target.value)}
              placeholder="Enter HTML email template here. Use {name} for the submitter's name."
              rows={15}
              className="font-mono text-sm"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-medium mb-2">Template Variables:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li><code className="bg-blue-100 px-2 py-0.5 rounded">{'{name}'}</code> - Submitter's name</li>
            </ul>
          </div>

          <div className="pt-4 border-t">
            <Button onClick={saveAutoReplyTemplate} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              Save Auto-Reply Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Example/Test Section */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-amber-900">Testing Email Configuration</CardTitle>
          <CardDescription className="text-amber-700">
            To test your email configuration, submit a test contact form from the public website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-amber-900 space-y-2">
            <p><strong>What will happen:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Contact form submission will be saved to database</li>
              <li>Email notification sent to all configured admin emails</li>
              <li>Auto-reply sent to the submitter's email address</li>
              <li>Submission visible in Contact Submissions page</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
